import React, { createContext, useContext, useState, ReactNode } from "react";
import { EmployeeProfile, EmployerProfile } from "../models/User";
import { useAuth } from "./AuthProvider";
import { getAuthHeaders } from "../utils/authHeaders";
import { API_URL } from "../config/api";

interface UserContextType {
  loading: boolean;
  error: string | null;
  
  // Employee Onboarding
  completeEmployeeOnboarding: (profileData: Partial<EmployeeProfile>) => Promise<any>;
  
  // Employer Onboarding
  completeEmployerOnboarding: (profileData: Partial<EmployerProfile>) => Promise<any>;
  
  // Profile Management
  getProfile: () => Promise<any>;
  updateProfile: (profileData: Partial<EmployeeProfile | EmployerProfile>) => Promise<any>;
  
  // Clear error
  clearError: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const completeEmployeeOnboarding = async (profileData: Partial<EmployeeProfile>) => {
    try {
      setLoading(true);
      setError(null);

      // Get user ID from the authenticated user context
      const userId = user?.id;
      if (!userId) {
        throw new Error("User must be authenticated to complete onboarding");
      }

      const response = await fetch(`${API_URL}/auth/updateUser/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        credentials: "include",
        body: JSON.stringify(profileData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Employee onboarding failed");
      }

      // Update user state to mark onboarding as completed.
      // Pass only the delta — updateUser merges with the latest user internally,
      // avoiding stale-closure overwrites of fields updated elsewhere.
      if (updateUser) {
        updateUser({
          onboardingCompleted: true,
          profileCompleted: true
        });
      }

      return data;
    } catch (err) {
      const errorObj = err as Error;
      console.error("❌ Employee onboarding error details:", {
        message: errorObj.message,
        stack: errorObj.stack,
      });
      setError(errorObj.message);
      throw errorObj;
    } finally {
      setLoading(false);
    }
  };

  const completeEmployerOnboarding = async (profileData: Partial<EmployerProfile>) => {
    try {
      setLoading(true);
      setError(null);

      // Get user ID from the authenticated user context
      const userId = user?.id;
      if (!userId) {
        throw new Error("User must be authenticated to complete onboarding");
      }

      const response = await fetch(`${API_URL}/auth/updateUser/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        credentials: "include",
        body: JSON.stringify(profileData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Employer onboarding failed");
      }

      // Update user state to mark onboarding as completed.
      // Pass only the delta — updateUser merges with the latest user internally,
      // avoiding stale-closure overwrites of fields updated elsewhere.
      if (updateUser) {
        updateUser({
          onboardingCompleted: true,
          profileCompleted: true
        });
      }

      return data;
    } catch (err) {
      const errorObj = err as Error;
      console.error("❌ Employer onboarding error details:", {
        message: errorObj.message,
        stack: errorObj.stack,
      });
      setError(errorObj.message);
      throw errorObj;
    } finally {
      setLoading(false);
    }
  };

  const getProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/auth/me`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch profile");
      }

      return data;
    } catch (err) {
      const errorObj = err as Error;
      console.error("❌ Profile fetch error details:", {
        message: errorObj.message,
        stack: errorObj.stack,
      });
      setError(errorObj.message);
      throw errorObj;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (profileData: Partial<EmployeeProfile | EmployerProfile>) => {
    try {
      setLoading(true);
      setError(null);

      // Get user ID from the authenticated user context
      const userId = user?.id;
      if (!userId) {
        throw new Error("User must be authenticated to update profile");
      }

      const response = await fetch(`${API_URL}/auth/updateUser/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        credentials: "include",
        body: JSON.stringify(profileData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Profile update failed");
      }

      return data;
    } catch (err) {
      const errorObj = err as Error;
      console.error("❌ Profile update error details:", {
        message: errorObj.message,
        stack: errorObj.stack,
      });
      setError(errorObj.message);
      throw errorObj;
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  return (
    <UserContext.Provider 
      value={{ 
        loading, 
        error, 
        completeEmployeeOnboarding, 
        completeEmployerOnboarding, 
        getProfile, 
        updateProfile, 
        clearError 
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
